import { useEffect, useMemo, useState } from "react";
import { Plus, Edit, Trash2, Search, Smartphone, X, Loader2, Save, RotateCcw, Sparkles, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* All 18 repair types — labels for inputs */
const ISSUE_LABELS = [
  ["display", "Displaytausch"],
  ["battery", "Akkutausch"],
  ["charging", "Ladebuchse"],
  ["backcover", "Backcover"],
  ["rear_camera", "Kamera hinten"],
  ["front_camera", "Frontkamera"],
  ["earpiece", "Hörmuschel"],
  ["speaker", "Lautsprecher"],
  ["microphone", "Mikrofon"],
  ["faceid", "Face ID / Sensor"],
  ["power_button", "Power Button"],
  ["volume_button", "Lautstärke Button"],
  ["vibration", "Vibrationsmotor"],
  ["water", "Wasserschaden"],
  ["datarescue", "Datenrettung"],
  ["software", "Software Problem"],
  ["diagnostic", "Diagnose"],
  ["other", "Sonstiges"],
];

const SOURCE_LABEL = {
  admin: { text: "manuell", className: "bg-[#DC1928]/15 text-[#FF4F5E] border-[#DC1928]/30" },
  mcservice: { text: "mcservice", className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  tier: { text: "geschätzt", className: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  fallback: { text: "Standard", className: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
};

export default function AdminModels() {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [search, setSearch] = useState("");

  // Effective active brand: user's choice if any, otherwise first brand in the list
  const activeBrand = selectedBrand || brands[0]?.brand_id || null;

  const [editing, setEditing] = useState(null);   // { brand_id, model_name, prices, is_custom, has_override }
  const [editPrices, setEditPrices] = useState({});
  const [editName, setEditName] = useState("");

  const [creating, setCreating] = useState(false);
  const [newModel, setNewModel] = useState({ brand_id: "apple", model_name: "", prices: {} });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/models");
    setBrands(data);
  };

  useEffect(() => { load(); }, []);

  const currentBrand = brands.find((b) => b.brand_id === activeBrand);
  const filteredModels = useMemo(() => {
    if (!currentBrand) return [];
    const q = search.trim().toLowerCase();
    if (!q) return currentBrand.models;
    return currentBrand.models.filter((m) => m.model_name.toLowerCase().includes(q));
  }, [currentBrand, search]);

  /* ---------- Edit existing model ---------- */
  const openEdit = (m) => {
    setEditing(m);
    setEditName(m.model_name);
    // Pre-fill only existing admin overrides
    const overrides = {};
    Object.entries(m.prices).forEach(([iid, info]) => {
      if (info.source === "admin") overrides[iid] = String(info.price);
    });
    setEditPrices(overrides);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const cleanPrices = {};
      Object.entries(editPrices).forEach(([iid, v]) => {
        const num = Number(String(v).replace(",", "."));
        if (!isNaN(num) && num >= 0 && String(v).trim() !== "") cleanPrices[iid] = Math.round(num);
      });
      await api.put(
        `/admin/models/${encodeURIComponent(editing.brand_id)}/${encodeURIComponent(editing.model_name)}`,
        { brand_id: editing.brand_id, model_name: editing.model_name, prices: cleanPrices, is_custom: editing.is_custom },
      );
      toast.success("Preise gespeichert");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const removeOverrides = async () => {
    if (!editing) return;
    if (!confirm(editing.is_custom
      ? `Modell "${editing.model_name}" komplett löschen?`
      : `Alle manuellen Preise für "${editing.model_name}" entfernen? Es werden wieder die Auto-Preise verwendet.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/models/${encodeURIComponent(editing.brand_id)}/${encodeURIComponent(editing.model_name)}`);
      toast.success(editing.is_custom ? "Modell gelöscht" : "Manuelle Preise entfernt");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Create new model ---------- */
  const openCreate = () => {
    setNewModel({ brand_id: activeBrand || "apple", model_name: "", prices: {} });
    setCreating(true);
  };

  const saveCreate = async () => {
    if (!newModel.model_name.trim()) {
      toast.error("Bitte Modellname angeben");
      return;
    }
    setBusy(true);
    try {
      const cleanPrices = {};
      Object.entries(newModel.prices).forEach(([iid, v]) => {
        const num = Number(String(v).replace(",", "."));
        if (!isNaN(num) && num >= 0 && String(v).trim() !== "") cleanPrices[iid] = Math.round(num);
      });
      await api.post("/admin/models", {
        brand_id: newModel.brand_id,
        model_name: newModel.model_name.trim(),
        prices: cleanPrices,
        is_custom: true,
      });
      toast.success(`Modell "${newModel.model_name}" erstellt`);
      setCreating(false);
      setSelectedBrand(newModel.brand_id);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erstellen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (brands.length === 0) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="models-loading">
        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-models-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Modelle & Reparaturpreise</h1>
          <p className="text-sm text-zinc-500 mt-1">Eigene Modelle anlegen oder Preise für bestehende Modelle anpassen.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-10 px-5" data-testid="models-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Neues Modell
        </Button>
      </div>

      {/* Brand tabs */}
      <div className="flex flex-wrap gap-2">
        {brands.map((b) => {
          const isActive = activeBrand === b.brand_id;
          const customCount = b.models.filter((m) => m.is_custom).length;
          return (
            <button
              key={b.brand_id}
              onClick={() => { setSelectedBrand(b.brand_id); setSearch(""); }}
              className={`px-4 h-10 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                isActive
                  ? "bg-[#DC1928] text-white shadow-[0_4px_14px_-4px_rgba(220,25,40,0.6)]"
                  : "bg-[#141417] border border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
              data-testid={`brand-tab-${b.brand_id}`}
            >
              {b.brand_name}
              <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${isActive ? "bg-white/20" : "bg-white/5 text-zinc-500"}`}>
                {b.models.length}
              </span>
              {customCount > 0 && (
                <Sparkles className={`w-3 h-3 ${isActive ? "text-white/80" : "text-amber-400"}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Modell suchen…"
          className="pl-10 h-11 bg-[#141417] border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928]"
          data-testid="models-search-input"
        />
      </div>

      {/* Models list */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="models-grid">
        {filteredModels.map((m) => (
          <ModelCard key={m.model_name} model={m} onEdit={() => openEdit(m)} />
        ))}
        {filteredModels.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 py-10 text-center text-zinc-500" data-testid="models-empty">
            <div className="font-display text-base">Kein Modell gefunden.</div>
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl bg-[#0F0F11] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="model-edit-dialog">
          {editing && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-[#FF2D40]" />
                  <DialogTitle className="font-display text-xl">{editing.model_name}</DialogTitle>
                  {editing.is_custom && (
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 font-semibold">
                      Eigenes Modell
                    </span>
                  )}
                </div>
                <DialogDescription className="text-zinc-500">
                  Eigene Preise überschreiben die automatischen mcservice-Preise. Feld leer lassen = Auto-Preis verwenden.
                </DialogDescription>
              </DialogHeader>

              <PriceGrid
                model={editing}
                values={editPrices}
                onChange={setEditPrices}
                testidPrefix="edit"
              />

              <VariantsSection brandId={editing.brand_id} modelName={editing.model_name} />

              <DialogFooter className="flex-row gap-2 sm:gap-3 mt-4">
                {editing.has_override || editing.is_custom ? (
                  <Button
                    variant="outline"
                    onClick={removeOverrides}
                    disabled={busy}
                    className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-full"
                    data-testid="model-edit-remove"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {editing.is_custom ? "Modell löschen" : "Auto zurücksetzen"}
                  </Button>
                ) : null}
                <div className="flex-1" />
                <Button
                  variant="outline"
                  onClick={() => setEditing(null)}
                  disabled={busy}
                  className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full"
                  data-testid="model-edit-cancel"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={saveEdit}
                  disabled={busy}
                  className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full"
                  data-testid="model-edit-save"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-3xl bg-[#0F0F11] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="model-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Neues Modell anlegen</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Z.B. <strong className="text-white">iPhone 18 Pro Max</strong> oder <strong className="text-white">Galaxy S26 Ultra</strong>.
              Preise können später ergänzt werden — leer = Schätzpreis wird automatisch berechnet.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Marke</Label>
              <Select value={newModel.brand_id} onValueChange={(v) => setNewModel((n) => ({ ...n, brand_id: v }))}>
                <SelectTrigger className="mt-1.5 bg-[#0A0A0B] border-white/10 text-white" data-testid="model-create-brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141417] border-white/10 text-white">
                  {brands.map((b) => (
                    <SelectItem key={b.brand_id} value={b.brand_id}>{b.brand_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Modellname</Label>
              <Input
                value={newModel.model_name}
                onChange={(e) => setNewModel((n) => ({ ...n, model_name: e.target.value }))}
                placeholder="z.B. iPhone 18 Pro Max"
                className="mt-1.5 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600"
                data-testid="model-create-name"
              />
            </div>
          </div>

          <div className="mt-4 p-3 rounded-xl bg-amber-500/5 border border-amber-500/15 flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400 leading-relaxed">
              Da das Modell <strong className="text-white">neu</strong> ist, sind keine mcservice-Preise hinterlegt. Setzen Sie hier
              direkt die Preise — leer gelassene Felder zeigen den vorgeschlagenen Wert eines ähnlichen Modells als Standard.
            </p>
          </div>

          <PriceGrid
            model={null}
            values={newModel.prices}
            onChange={(p) => setNewModel((n) => ({ ...n, prices: p }))}
            testidPrefix="create"
            className="mt-4"
          />

          <DialogFooter className="flex-row gap-2 sm:gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setCreating(false)}
              disabled={busy}
              className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full"
              data-testid="model-create-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={saveCreate}
              disabled={busy}
              className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full"
              data-testid="model-create-save"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Modell anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function ModelCard({ model, onEdit }) {
  // Show 4 key prices as preview
  const previewIssues = ["display", "battery", "backcover", "datarescue"];
  const sources = new Set(Object.values(model.prices).map((p) => p.source));
  const dominant = sources.has("admin") ? "admin" : sources.has("mcservice") ? "mcservice" : Array.from(sources)[0] || "fallback";
  const sourceMeta = SOURCE_LABEL[dominant];

  return (
    <div className="p-4 rounded-2xl bg-[#141417] border border-white/5 hover:border-white/15 transition-colors" data-testid={`model-card-${model.model_name.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-white text-sm leading-tight">{model.model_name}</h3>
            {model.is_custom && (
              <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">eigen</span>
            )}
            {model.has_override && !model.is_custom && (
              <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full bg-[#DC1928]/15 text-[#FF4F5E] border border-[#DC1928]/30">geändert</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${sourceMeta.className}`}>
              {sourceMeta.text}
            </span>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="shrink-0 px-3 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white flex items-center gap-1.5 transition-colors"
          data-testid={`edit-${model.model_name.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <Edit className="w-3 h-3" /> Preise
        </button>
      </div>

      <div className="grid grid-cols-2 gap-1.5 mt-2">
        {previewIssues.map((iid) => {
          const info = model.prices[iid];
          if (!info) return null;
          return (
            <div key={iid} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-[#0A0A0B] border border-white/5 text-xs">
              <span className="text-zinc-500 truncate capitalize">{iid.replace(/_/g, " ")}</span>
              <span className={`font-display font-semibold numeric ml-2 ${info.source === "admin" ? "text-[#FF4F5E]" : "text-white"}`}>
                €{info.price}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PriceGrid({ model, values, onChange, testidPrefix, className = "" }) {
  return (
    <div className={`mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2.5 ${className}`} data-testid={`${testidPrefix}-price-grid`}>
      {ISSUE_LABELS.map(([iid, label]) => {
        // Auto-suggested price = whatever the API would compute via mcservice/tier
        const autoInfo = model?.prices?.[iid];
        const autoPrice = autoInfo?.price;
        const autoSource = autoInfo?.source;
        const autoLabel = autoSource && autoSource !== "admin" ? SOURCE_LABEL[autoSource]?.text : null;
        return (
          <div key={iid} className="flex items-center gap-2 p-2.5 rounded-xl bg-[#0A0A0B] border border-white/5">
            <Label htmlFor={`${testidPrefix}-${iid}`} className="flex-1 text-xs text-zinc-400 cursor-pointer">{label}</Label>
            <div className="relative w-28 shrink-0">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">€</span>
              <Input
                id={`${testidPrefix}-${iid}`}
                type="number"
                inputMode="numeric"
                value={values[iid] ?? ""}
                onChange={(e) => onChange({ ...values, [iid]: e.target.value })}
                placeholder={autoPrice ? `auto ${autoPrice}` : ""}
                className="h-9 pl-6 pr-2 bg-[#141417] border-white/10 text-white placeholder:text-zinc-600 text-sm tabular-nums focus:border-[#DC1928]"
                data-testid={`${testidPrefix}-input-${iid}`}
              />
              {autoLabel && (
                <span className="absolute -bottom-4 right-0 text-[9px] text-zinc-600 uppercase tracking-wider">{autoLabel}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Variants section (mcservice-style: Original / Refurbished / OLED OEM / LCD OEM) ---------- */

const VARIANT_ISSUE_LABELS = {
  display: "Displaytausch",
  backcover: "Backcover / Rückseite",
  rear_camera: "Kamera hinten",
  front_camera: "Frontkamera",
  battery: "Akkutausch",
};

function VariantsSection({ brandId, modelName }) {
  const [templates, setTemplates] = useState(null);
  const [variantsByIssue, setVariantsByIssue] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const tplRes = await api.get("/catalog/variants/templates");
        const tpl = tplRes.data || {};
        const issueIds = Object.keys(tpl);
        const pairs = await Promise.all(
          issueIds.map((iid) =>
            api
              .get(`/admin/variants/${encodeURIComponent(brandId)}/${encodeURIComponent(modelName)}/${iid}`)
              .then((rr) => [iid, rr.data?.variants || []])
              .catch(() => [iid, []]),
          ),
        );
        if (cancelled) return;
        const map = {};
        pairs.forEach(([iid, vs]) => { map[iid] = vs; });
        setTemplates(tpl);
        setVariantsByIssue(map);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId, modelName]);

  const updateIssueVariants = (iid, newList) => {
    setVariantsByIssue((m) => ({ ...m, [iid]: newList }));
  };

  const saveIssue = async (iid) => {
    const clean = (variantsByIssue[iid] || [])
      .map((v) => ({ name: (v.name || "").trim(), price: Number(String(v.price).replace(",", ".")) || 0 }))
      .filter((v) => v.name && v.price > 0);
    try {
      await api.put(`/admin/variants/${encodeURIComponent(brandId)}/${encodeURIComponent(modelName)}/${iid}`, {
        variants: clean,
      });
      toast.success(`Varianten für ${VARIANT_ISSUE_LABELS[iid]} gespeichert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const resetToTemplate = (iid) => {
    const tpl = templates?.[iid] || [];
    updateIssueVariants(iid, tpl.map((name) => ({ name, price: "" })));
  };

  if (loading) {
    return (
      <div className="mt-6 pt-6 border-t border-white/5 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Varianten laden…
      </div>
    );
  }

  if (!templates || Object.keys(templates).length === 0) return null;

  return (
    <div className="mt-6 pt-6 border-t border-white/5" data-testid="variants-section">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div>
          <h4 className="font-display text-base font-semibold text-white">Reparatur-Varianten</h4>
          <p className="text-xs text-zinc-500 mt-0.5">
            Pro Reparaturart kannst du mehrere Qualitätsstufen mit eigenen Preisen anlegen
            (z.B. Original / Refurbished / OLED OEM / LCD OEM). Ohne Varianten gilt der Standardpreis oben.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {Object.keys(templates).map((iid) => (
          <IssueVariants
            key={iid}
            issueId={iid}
            label={VARIANT_ISSUE_LABELS[iid] || iid}
            template={templates[iid] || []}
            variants={variantsByIssue[iid] || []}
            onChange={(list) => updateIssueVariants(iid, list)}
            onSave={() => saveIssue(iid)}
            onResetTemplate={() => resetToTemplate(iid)}
          />
        ))}
      </div>
    </div>
  );
}

function IssueVariants({ issueId, label, template, variants, onChange, onSave, onResetTemplate }) {
  const isEmpty = variants.length === 0;

  const addRow = () => onChange([...variants, { name: "", price: "" }]);
  const updateRow = (i, key, val) => {
    const next = [...variants];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  const removeRow = (i) => onChange(variants.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-xl bg-[#0A0A0B] border border-white/5 p-3" data-testid={`variants-issue-${issueId}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{label}</span>
          {isEmpty ? (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-500/10 text-zinc-500 border border-zinc-500/15">Standardpreis</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              {variants.length} Variante{variants.length === 1 ? "" : "n"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {isEmpty && template.length > 0 && (
            <button
              type="button"
              onClick={onResetTemplate}
              className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/5"
              data-testid={`variants-template-${issueId}`}
            >
              Vorlage laden
            </button>
          )}
          <button
            type="button"
            onClick={addRow}
            className="text-[11px] text-zinc-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/5 inline-flex items-center gap-1"
            data-testid={`variants-add-${issueId}`}
          >
            <Plus className="w-3 h-3" /> Variante
          </button>
          <button
            type="button"
            onClick={onSave}
            className="text-[11px] font-medium px-2.5 py-1 rounded-md bg-[#DC1928] text-white hover:bg-[#B01829] inline-flex items-center gap-1"
            data-testid={`variants-save-${issueId}`}
          >
            <Save className="w-3 h-3" /> Speichern
          </button>
        </div>
      </div>

      {variants.length > 0 && (
        <div className="space-y-1.5">
          {variants.map((v, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`variant-row-${issueId}-${i}`}>
              <Input
                value={v.name || ""}
                onChange={(e) => updateRow(i, "name", e.target.value)}
                placeholder="z.B. Original"
                className="h-9 bg-[#141417] border-white/10 text-white text-sm flex-1"
                data-testid={`variant-name-${issueId}-${i}`}
              />
              <div className="relative w-28 shrink-0">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 text-sm pointer-events-none">€</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={v.price ?? ""}
                  onChange={(e) => updateRow(i, "price", e.target.value)}
                  placeholder="349"
                  className="h-9 pl-6 pr-2 bg-[#141417] border-white/10 text-white text-sm tabular-nums"
                  data-testid={`variant-price-${issueId}-${i}`}
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="w-9 h-9 rounded-md hover:bg-red-500/10 text-zinc-500 hover:text-red-400 flex items-center justify-center"
                aria-label="Variante entfernen"
                data-testid={`variant-remove-${issueId}-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

